"use client";

import React from "react";

import Link from "next/link";

import ProductGrid from "@/components/product/ProductGrid";
import { ProductGridSkeleton } from "@/components/product/ProductGridSkeleton";
import { ResultsExperience } from "@/components/results/ResultsExperience";
import type { ResultsExperienceInitialState } from "@/components/results/ResultsExperienceInitialState";
import { ResultsFiltersPanel } from "@/components/results/ResultsFiltersPanel";
import { useBrandResultsController } from "@/hooks/useBrandResultsController";
import { PRICE_RANGE_MAX_DEFAULT, PRICE_RANGE_MIN_DEFAULT } from "@/hooks/useResultsFilters";

interface BrandResultsExperienceProps {
  brandName: string;
  canonicalBrandPath: string;
  introText: string;
  initialState: ResultsExperienceInitialState;
  crawlPagination: {
    currentPage: number;
    previousHref: string | null;
    nextHref: string | null;
  };
}

export function BrandResultsExperience({
  brandName,
  canonicalBrandPath,
  introText,
  initialState,
  crawlPagination,
}: BrandResultsExperienceProps): React.ReactElement {
  const controller = useBrandResultsController({
    brandName,
    initialState,
  });

  const filtersContent = (
    <ResultsFiltersPanel
      category={{
        categories: controller.categoryTree,
        navigationStack: controller.filters.categoryNavigationStack,
        onNavigationChange: controller.updateCategoryNavigationStack,
        onCategoryNavigate: controller.handleCategoryNavigate,
        currentCategoryPath: controller.currentCategoryPath,
        selectedLeafPaths: controller.filters.categories,
        onLeafToggle: controller.handleLeafCategoryToggle,
        isLoadingTaxonomy: controller.isLoadingTaxonomy,
      }}
      price={{
        min: PRICE_RANGE_MIN_DEFAULT,
        max: PRICE_RANGE_MAX_DEFAULT,
        range: controller.filters.priceRange,
        onChange: controller.handlePriceChange,
      }}
      colors={{
        selected: controller.filters.colors,
        onChange: controller.handleColorChange,
      }}
      brands={{
        available: [],
        selected: [],
        onChange: () => undefined,
        isLoading: false,
      }}
      similarBrands={{
        selected: [],
        onChange: () => undefined,
      }}
      advanced={{
        searchParams: controller.searchFilters,
        selectedAttributes: controller.selectedAdvancedAttributesMap,
        onToggle: controller.handleAdvancedAttributeChange,
      }}
      visibility={{
        showBrandsFilter: false,
        showSimilarToBrandsFilter: false,
      }}
    />
  );

  const resultsContent = controller.isSearching ? (
    <ProductGridSkeleton />
  ) : (
    <>
      {controller.hasProducts ? (
        <ProductGrid
          searchParams={controller.searchFilters}
          products={controller.sortedResults}
          skipFetch
        />
      ) : (
        <div className="rounded-3xl border border-gray-200 bg-white px-6 py-16 text-center text-body text-ink-secondary">
          {controller.hasMoreResults
            ? "No products to display on this page."
            : "No products are available for this brand yet."}
        </div>
      )}
      {controller.hasMoreResults && (
        <div className="mt-8 flex justify-center pb-8">
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={controller.loadMoreResults}
              disabled={controller.isLoadingMore}
              data-testid="brand-results-load-more"
              className="inline-flex items-center gap-2 rounded-full bg-black px-8 py-3 text-sm font-medium text-white transition-all hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {controller.isLoadingMore
                ? "Loading..."
                : controller.loadMoreError
                  ? "Failed - try again"
                  : "Load More"}
            </button>
            {controller.loadMoreError && (
              <p className="text-center text-caption text-red-600">{controller.loadMoreError}</p>
            )}
          </div>
        </div>
      )}
      {(crawlPagination.previousHref || crawlPagination.nextHref) && (
        <nav
          aria-label={`${brandName} product pages`}
          className="flex items-center justify-center gap-4 pb-8 text-sm"
          data-testid="brand-results-crawl-pagination"
        >
          {crawlPagination.previousHref ? (
            <Link
              href={crawlPagination.previousHref}
              rel="prev"
              className="rounded-full border border-gray-300 px-5 py-2 font-medium text-ink transition-colors hover:bg-gray-50"
            >
              Previous
            </Link>
          ) : null}
          <span className="text-ink-secondary">Page {crawlPagination.currentPage}</span>
          {crawlPagination.nextHref ? (
            <Link
              href={crawlPagination.nextHref}
              rel="next"
              className="rounded-full border border-gray-300 px-5 py-2 font-medium text-ink transition-colors hover:bg-gray-50"
            >
              Next
            </Link>
          ) : null}
        </nav>
      )}
    </>
  );

  return (
    <div className="bg-white">
      <div className="px-4 pb-6 pt-6 lg:px-[30px] lg:pb-8 lg:pt-8">
        <Link
          href={canonicalBrandPath}
          className="mb-5 inline-flex items-center gap-2 text-caption text-ink-secondary transition-colors hover:text-ink"
        >
          <span>←</span>
          <span>Back to Brand</span>
        </Link>

        <div className="max-w-3xl space-y-2">
          <p className="text-overline tracking-widest text-ink-muted">Brand Shop</p>
          <p className="text-body text-ink-secondary">{introText}</p>
        </div>
      </div>

      <ResultsExperience
        header={{
          searchParams: controller.searchFilters,
          hasResults: controller.hasProducts,
          searchTextQuery: initialState.searchTextQuery,
          searchBreadcrumbPath: controller.searchBreadcrumbPath,
          isSearching: controller.isSearching,
          onCategoryNavigate: controller.handleCategoryNavigate,
          isCategoryNavigationEnabled: controller.isCategoryNavigationEnabled,
        }}
        stickyFilters={{
          isLoadingAssistant: false,
          filtersExpanded: controller.filtersExpanded,
          setFiltersExpanded: controller.setFiltersExpanded,
          mobileSuggestionsExpanded: controller.mobileSuggestionsExpanded,
          setMobileSuggestionsExpanded: controller.setMobileSuggestionsExpanded,
          isSticky: controller.isSticky,
          filtersHeight: controller.filtersHeight,
          filtersSectionTopRef: controller.filtersSectionTopRef,
          filtersContentRef: controller.filtersContentRef,
          activeFilters: controller.activeFilters,
          suggestedFilters: [],
          selectedBrands: [],
          similarToBrandsPills: [],
          activeBrandsCount: 0,
          assistantJustification: null,
          onMobileFiltersOpen: () => controller.setIsMobileFiltersOpen(true),
          onClearSuggestedFilters: async () => undefined,
          onClearSuggestedBrands: () => undefined,
        }}
        layout={{
          hasSearchParams: true,
          hasProducts: controller.hasProducts,
          productCount: controller.productCount,
          appliedFiltersCount: controller.appliedFiltersCount,
          isMobileFiltersOpen: controller.isMobileFiltersOpen,
          onMobileFiltersOpenChange: controller.setIsMobileFiltersOpen,
          onClearAllFilters: controller.restoreInitialState,
          sortOrder: controller.sortOrder,
          isSortMenuOpen: controller.isSortMenuOpen,
          onToggleSortMenu: controller.toggleSortMenu,
          onSortSelect: controller.handleSortSelection,
          sortDropdownListId: controller.sortDropdownListId,
          filtersContent,
        }}
        content={resultsContent}
      />
    </div>
  );
}
